import React, { FC, memo } from "react";
import { keyframes } from "@emotion/react";

import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { Icon } from "@UI/Components/Icon/Icon";
import { PaperPlane } from "@UI/Components/Icons/PaperPlane";
import { ArrowUpRightFromSquare } from "@UI/Components/Icons/ArrowUpRightFromSquare";
import { VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import messages from "../../messages";

export interface ITaskCreationSuccessModalProps {
  isOpen: boolean;
  onSeeMyRequest: () => void;
  onGetTheApp: () => void;
}

/**
 * `planeEnter` arcs the plane in from the left along a single gentle wave,
 * nose tilting with the curve. `planeIdle` picks up the rock once it lands.
 * Its delay matches the entrance's duration so only one animation is ever
 * actively driving `transform` at a time — a clean handoff with no JS state.
 */
const planeEnter = keyframes`
  0% { transform: translate(-60px, 0) rotate(-14deg) scale(0.75); opacity: 0; }
  60% { transform: translate(-28px, -10px) rotate(6deg) scale(1); opacity: 1; }
  100% { transform: translate(-10px, 5px) rotate(7deg) scale(0.95); opacity: 1; }
`;

const planeIdle = keyframes`
  0%, 100% { transform: translate(-10px, 5px) rotate(7deg) scale(0.95); }
  50% { transform: translate(10px, 0) rotate(12deg) scale(1.05);}
`;

/** Shown after a successful publish, in place of the automatic redirect (RT-4321). */
export const TaskCreationSuccessModal: FC<ITaskCreationSuccessModalProps> = memo(
  ({ isOpen, onSeeMyRequest, onGetTheApp }) => {
    const { isMobile } = useMediaQuery();

    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onSeeMyRequest}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        bodyPadding={isMobile ? "40px 16px" : "40px 24px"}
      >
        <VStack justifyContent="center" spacing="4">
          <Icon
            as={PaperPlane}
            w="16"
            h="16"
            color="green.500"
            marginBottom="4"
            sx={{
              animation: `${planeEnter} 0.85s ease-out, ${planeIdle} 5.5s ease-in-out 0.85s infinite`,
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          />
          <Heading>
            <FormattedMessage {...messages["taskCreation.successModal.title"]} />
          </Heading>
          <Text textAlign="center" fontWeight="bold" m="0" color="gray.800">
            <FormattedMessage
              {...messages["taskCreation.successModal.subtext"]}
            />
          </Text>
          <Text textAlign="center">
            <FormattedMessage {...messages["taskCreation.successModal.body"]} />
          </Text>
          <Button w="full" size="lg" colorScheme="seaGreen" onClick={onSeeMyRequest}>
            <FormattedMessage
              {...messages["taskCreation.successModal.seeMyRequest"]}
            />
          </Button>
          <Button
            w="full"
            size="lg"
            variant="secondary"
            rightIcon={
              <Icon as={ArrowUpRightFromSquare} width="14px" height="14px" />
            }
            onClick={onGetTheApp}
          >
            <FormattedMessage
              {...messages["taskCreation.successModal.getTheApp"]}
            />
          </Button>
        </VStack>
      </ResponsiveModal>
    );
  }
);

TaskCreationSuccessModal.displayName = "TaskCreationSuccessModal";
